import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDraft, deleteDraft } from '../services/draftService';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { isValidVk } from '../utils/validators';

/**
 * Hook that listens for authentication and syncs any pending guest drafts.
 */
export const useGuestDraftSync = () => {
    const { isAuthenticated, user } = useAuth();
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncedLostReport, setSyncedLostReport] = useState(null);
    const [syncedFoundReport, setSyncedFoundReport] = useState(null);

    const syncAttemptedRef = useRef(false); // Lock to prevent double-fire
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAuthenticated || !user) {
            // Reset locks on logout so new drafts can be synced on next login
            syncAttemptedRef.current = false;
            sessionStorage.removeItem('guest_draft_synced_lost');
            sessionStorage.removeItem('guest_draft_synced_found');
            return;
        }

        const syncDrafts = async () => {
            if (syncAttemptedRef.current) return; // Prevent duplicate sync
            syncAttemptedRef.current = true; // LOCK

            const types = ['lost', 'found'];
            let syncedAny = false;
            let newlySyncedReport = false;

            for (const type of types) {
                // Failsafe: check session storage to avoid re-syncing in same session
                const sessionKey = `guest_draft_synced_${type}`;
                if (sessionStorage.getItem(sessionKey)) {
                    console.log(`[GuestSync] Skipping ${type} - already synced in this session.`);
                    continue;
                }


                const draft = await getDraft(type);
                if (!draft) {
                    continue;
                }
                console.log(`[GuestSync] Checking draft for type '${type}':`, draft);

                // VALIDATION: Ensure draft has all required fields before syncing
                if (type === 'lost') {
                    const hasName = draft.data?.petName?.trim();
                    const hasLat = draft.data?.locationLat != null;
                    const hasLng = draft.data?.locationLng != null;
                    const hasLocationDesc = draft.data?.locationAddress?.trim() || draft.data?.locationMunicipality?.trim();
                    const hasCountryCode = draft.data?.locationCountryCode?.trim() || draft.data?.countryCode?.trim();
                    const hasPhotos = draft.photos && Array.isArray(draft.photos) && draft.photos.length > 0;

                    console.log(`[GuestSync] LOST draft validation:`, {
                        hasName,
                        hasLat,
                        hasLng,
                        hasLocationDesc,
                        hasCountryCode,
                        hasPhotos: hasPhotos ? `${draft.photos.length} photo(s)` : 'no photos',
                        data: draft.data
                    });

                    if (!hasName || !hasLat || !hasLng || !hasLocationDesc || !hasCountryCode || !hasPhotos) {
                        console.warn(`[GuestSync] Skipping incomplete ${type} draft. Missing required fields:`, {
                            hasName,
                            hasLat,
                            hasLng,
                            hasLocationDesc,
                            hasCountryCode,
                            hasPhotos: hasPhotos ? `${draft.photos.length} photo(s)` : 'no photos'
                        });
                        continue;
                    }
                } else if (type === 'found') {
                    const hasLat = draft.data?.locationLat != null;
                    const hasLng = draft.data?.locationLng != null;
                    const hasLocationDesc = draft.data?.locationAddress?.trim() || draft.data?.locationMunicipality?.trim();
                    const hasCountryCode = draft.data?.locationCountryCode?.trim() || draft.data?.countryCode?.trim();
                    const hasPhotos = draft.photos && Array.isArray(draft.photos) && draft.photos.length > 0;

                    if (!hasLat || !hasLng || !hasLocationDesc || !hasCountryCode || !hasPhotos) {
                        console.warn(`[GuestSync] Skipping incomplete ${type} draft. Missing required fields:`, {
                            hasLat,
                            hasLng,
                            hasLocationDesc,
                            hasCountryCode,
                            hasPhotos: hasPhotos ? `${draft.photos.length} photo(s)` : 'no photos'
                        });
                        continue;
                    }
                }


                setIsSyncing(true);
                try {
                    console.log(`[GuestSync] Found complete guest draft (${type}), syncing...`, draft);

                    const formData = new FormData();
                    formData.append('report_type', type);

                    // Map draft data to backend expectations
                    if (draft.data) {
                        if (draft.data.petName) formData.append('pet_name', draft.data.petName);
                        if (draft.data.occurredDate) formData.append('event_occurred_at', draft.data.occurredDate);
                        if (draft.data.locationLat) formData.append('location_lat', draft.data.locationLat);
                        if (draft.data.locationLng) formData.append('location_lng', draft.data.locationLng);
                        if (draft.data.municipalitySlug) formData.append('municipality_slug', draft.data.municipalitySlug);

                        const countryCode = draft.data.countryCode || draft.data.locationCountryCode;
                        if (countryCode) formData.append('country_code', countryCode);

                        if (draft.data.userDescription) formData.append('user_description', draft.data.userDescription);
                        if (draft.data.contactName) formData.append('contact_name', draft.data.contactName);
                        if (draft.data.contactNumber) formData.append('contact_number', draft.data.contactNumber);
                        if (draft.data.contactVk) formData.append('contact_vk', draft.data.contactVk);
                        const isCoauthorValid = isValidVk(draft.data.contactVk) && Boolean(draft.data.vkCoauthor);
                        formData.append('vk_coauthor', String(isCoauthorValid));
                        if (draft.data.locationMunicipality) formData.append('municipality', draft.data.locationMunicipality);
                        if (draft.data.locationMunicipalityPlaceId) formData.append('municipality_place_id', draft.data.locationMunicipalityPlaceId);

                        const fullAddr = draft.data.locationAddress || draft.data.locationMunicipality;
                        if (fullAddr) formData.append('location_description', fullAddr);


                        // Features JSON - Always include all keys even if empty
                        const features = {
                            collar: draft.data.featCollarText || '',
                            clothes: draft.data.featClothesText || '',
                            microchip: draft.data.featMicrochipText || ''
                        };

                        // Always send features object
                        formData.append('features', JSON.stringify(features));
                    }

                    // Photos
                    if (draft.photos && Array.isArray(draft.photos)) {
                        draft.photos.forEach(photo => {
                            if (photo instanceof File || photo instanceof Blob) {
                                formData.append('photos[]', photo);
                            }
                        });
                    }

                    // Submit
                    const response = await api.post('/api/v1/account/pet_reports/from_guest', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });

                    // API returns the object directly, check for ID
                    if (response.data && response.data.id) {
                        // Mark as synced immediately to prevent loops
                        sessionStorage.setItem(sessionKey, 'true');

                        // cleanup specific draft
                        await deleteDraft(type);
                        syncedAny = true;

                        // If it is a LOST report, we want to pop up the dialog
                        if (type === 'lost') {
                            newlySyncedReport = true;
                            setSyncedLostReport(response.data);
                        } else if (type === 'found') {
                            newlySyncedReport = true;
                            setSyncedFoundReport(response.data);
                        }

                    }

                } catch (error) {
                    console.error(`Failed to sync guest draft (${type}):`, error);
                }
            }

            setIsSyncing(false);

            if (syncedAny && !newlySyncedReport) {
                navigate('/dashboard');
            }

        };

        syncDrafts();
    }, [isAuthenticated, user, navigate]); // Added syncedLostReport to dependancy to be safe but logic is inside effect

    const clearSyncedReport = () => {
        setSyncedLostReport(null);
        setSyncedFoundReport(null);
    };

    return { isSyncing, syncedLostReport, syncedFoundReport, clearSyncedReport };

};
